<template>
	<div>
		<Titlebar />
		<p>{{ greeting }}</p>
		<div class="cards">
			<Card>
				<section>
					<h2>Current competition</h2>
					<template v-if="is('error')">
						<p>Error loading running competition.</p>
					</template>
					<template v-else-if="is('loading')">
						<p>Loading...</p>
					</template>
					<template v-else-if="event">
						<p>
							The <strong>{{ event.name }}</strong> competition has been running since
							<strong>{{ formatEventDate(event.dates.from) }}</strong
							>, and will finish on <strong>{{ formatEventDate(event.dates.to) }}</strong
							>.
						</p>
					</template>
					<template v-else>
						<p>There aren't any competitions running at the moment.</p>
					</template>
				</section>
				<section v-if="is('idle')">
					<template v-if="event">
						<h3>Quick actions</h3>
						<div class="quick-actions">
							<button @click="editActiveEvent">Manage this competition</button>
							<button @click="disableActive = true">Disable this competition</button>
						</div>
					</template>
					<template v-else>
						<h3>Quick actions</h3>
						<div class="quick-actions">
							<button @click="manageEvents">Manage competitions</button>
							<button @click="createEvent">Create a competition</button>
						</div>
					</template>
				</section>
				<section v-if="is('idle') && event">
					<h3>Competition stats</h3>
					<EventStats :event="event._id" />
				</section>
			</Card>
			<Card>
				<section>
					<h2>All-time stats</h2>
					<EventStats :event="GLOBAL_EVENT" />
				</section>
			</Card>
		</div>
		<!-- Confirm disable competition modal -->
		<Modal v-if="disableActive" @close="disableActive = false" title="Disable this competition?">
			<p>
				This will disable the <strong>{{ event.name }}</strong> competition and immediately
				deactivate it in the Recycle Race game. You can re-enable it from the
				<router-link :to="links.manage">Manage Competitions</router-link> page.
			</p>
			<div class="modal-buttons">
				<button class="primary" type="button" @click="disableActiveEvent">Disable</button>
				<button class="secondary" type="button" @click="disableActive = false">Cancel</button>
			</div>
		</Modal>
	</div>
</template>

<script>
import { DateTime } from 'luxon'
import { GLOBAL_EVENT, ACTIVE_EVENT } from 'recycle-race-common/lib/constants'
import Titlebar from '@/components/Titlebar'
import Card from '@/components/Card'
import EventStats from '@/components/EventStats'
import Modal from '@/components/Modal'

import state from '@/mixins/state'
import toast from '@/mixins/toast'
import { EventProvider } from '@/lib/dataProvider'

export default {
	name: 'Dashboard',
	data() {
		return {
			event: null,
			time: DateTime.local(),
			timeInterval: null,
			disableActive: false,
			GLOBAL_EVENT,
		}
	},
	computed: {
		greeting() {
			const hour = this.time.hour
			if (hour >= 18) {
				return 'Good evening.'
			} else if (hour >= 12) {
				return 'Good afternoon.'
			} else {
				return 'Good morning.'
			}
		},
		links() {
			return {
				manage: {
					name: 'Manage Competitions',
				},
				create: {
					name: 'Create Competition',
				},
				current: {
					name: 'Edit Competition',
					params: {
						id: this.event ? this.event._id : null,
					},
				},
			}
		},
	},
	methods: {
		// Quick action buttons
		editActiveEvent() {
			this.$router.push(this.links.current)
		},
		async disableActiveEvent() {
			this.setState('loading')
			try {
				// Disable the event
				const eventName = this.event.name
				await EventProvider.update(ACTIVE_EVENT, { enabled: false })
				this.toast(`Successfully disabled the *${eventName}* competition.`)

				// Clear the event
				this.event = null
			} catch (err) {
				this.toastError('Failed to disable the competition.')
			}

			// Reset state and hide modal
			this.disableActive = false
			this.setState('idle')
		},
		manageEvents() {
			this.$router.push(this.links.manage)
		},
		createEvent() {
			this.$router.push(this.links.create)
		},
		// Other methods
		updateTime() {
			this.time = DateTime.local()
		},
		formatEventDate(iso) {
			return DateTime.fromISO(iso).toLocaleString({
				month: 'long',
				day: 'numeric',
				hour: 'numeric',
				minute: '2-digit',
				timeZoneName: 'short',
			})
		},
		async getActiveEvent() {
			this.setState('loading')
			try {
				this.event = await EventProvider.get(ACTIVE_EVENT)
				this.setState('idle')
			} catch (err) {
				switch (err.code) {
					case 404:
						this.event = null
						this.setState('idle')
						break
					default:
						this.setState('error')
						break
				}
			}
		},
	},
	created() {
		// Download active event on init
		this.getActiveEvent()

		// Set interval to update time every minute
		this.timeInterval = window.setInterval(this.updateTime, 1000)
	},
	mixins: [state(), toast],
	components: {
		Titlebar,
		Card,
		EventStats,
		Modal,
	},
}
</script>

<style lang="scss" scoped>
@import '~scss/layout';
@import '~scss/components';
@import '~scss/mq';

.cards {
	@include not-last {
		margin-bottom: 2rem;
	}

	.card {
		section {
			@include truncate-margin;
		}
		@include not-last {
			margin-bottom: 1.5rem;
		}
	}

	.quick-actions {
		button {
			@include button;
			display: block;
			width: 100%;
		}

		@include not-last {
			margin-bottom: 0.5rem;
		}
	}

	@include mq(md) {
		.card {
			@include not-last {
				margin-bottom: 2.5rem;
			}
		}

		.quick-actions {
			button {
				display: inline-block;
				width: auto;
			}

			@include not-last {
				margin-bottom: 0;
				margin-right: 1rem;
			}
		}
	}
}

.modal-buttons {
	@include modal-buttons;
}
</style>
